import React from 'react';
import { Button, Tooltip } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { ForecastAvailability, ActivityAvailability } from './types';

export interface TimelineLegendProps {
  theme: 'light' | 'dark';
  legendExpanded: boolean;
  onLegendExpandedChange: (expanded: boolean) => void;
  forecastAvailability?: ForecastAvailability;
  radarActivityAvailability?: ActivityAvailability;
  stationActivityAvailability?: ActivityAvailability;
  compact?: boolean;
}

export const TimelineLegend: React.FC<TimelineLegendProps> = ({
  theme,
  legendExpanded,
  onLegendExpandedChange,
  forecastAvailability,
  radarActivityAvailability,
  stationActivityAvailability,
  compact = false,
}) => {
  const hasAnyData =
    forecastAvailability?.daily ||
    forecastAvailability?.hourly ||
    forecastAvailability?.hourlyIssuance ||
    radarActivityAvailability ||
    stationActivityAvailability;

  if (!hasAnyData) return null;

  return (
    <div style={{ display: 'flex', gap: compact ? '4px' : '8px', fontSize: compact ? '9px' : '11px', alignItems: 'center', flexWrap: 'nowrap' }}>
      {legendExpanded && (
        <>
          {stationActivityAvailability?.ranges?.length && (
            <Tooltip title="Time periods with weather station data available">
              <span style={{ display: 'flex', alignItems: 'center', gap: compact ? '2px' : '3px', cursor: 'help' }}>
                <span
                  style={{
                    width: compact ? '8px' : '10px',
                    height: compact ? '3px' : '5px',
                    backgroundColor: theme === 'dark' ? 'rgba(19, 194, 194, 0.4)' : 'rgba(19, 194, 194, 0.3)',
                    borderRadius: '2px',
                    display: 'inline-block',
                  }}
                />
                Stations
              </span>
            </Tooltip>
          )}
          {radarActivityAvailability?.ranges?.length && (
            <Tooltip title="Time periods with radar imagery available">
              <span style={{ display: 'flex', alignItems: 'center', gap: compact ? '2px' : '3px', cursor: 'help' }}>
                <span
                  style={{
                    width: compact ? '8px' : '10px',
                    height: compact ? '3px' : '5px',
                    backgroundColor: theme === 'dark' ? 'rgba(255, 77, 79, 0.4)' : 'rgba(255, 77, 79, 0.3)',
                    borderRadius: '2px',
                    display: 'inline-block',
                  }}
                />
                Radar
              </span>
            </Tooltip>
          )}
          {forecastAvailability?.daily?.length && (
            <Tooltip title="Timestamps when a daily forecast was issued">
              <span style={{ display: 'flex', alignItems: 'center', gap: compact ? '2px' : '3px', cursor: 'help' }}>
                <span
                  style={{
                    width: '2px',
                    height: compact ? '7px' : '10px',
                    backgroundColor: theme === 'dark' ? 'rgba(250, 173, 20, 0.8)' : 'rgba(250, 173, 20, 0.7)',
                    display: 'inline-block',
                  }}
                />
                Daily
              </span>
            </Tooltip>
          )}
          {forecastAvailability?.hourlyIssuance?.length && (
            <Tooltip title="Timestamps when an hourly forecast was issued">
              <span style={{ display: 'flex', alignItems: 'center', gap: compact ? '2px' : '3px', cursor: 'help' }}>
                <span
                  style={{
                    width: '2px',
                    height: compact ? '7px' : '10px',
                    backgroundColor: theme === 'dark' ? 'rgba(82, 196, 26, 0.8)' : 'rgba(82, 196, 26, 0.7)',
                    display: 'inline-block',
                  }}
                />
                Hourly
              </span>
            </Tooltip>
          )}
        </>
      )}
      <Tooltip title={legendExpanded ? 'Hide legend' : 'Show legend'}>
        <Button
          type="text"
          size="small"
          icon={legendExpanded ? <UpOutlined /> : <DownOutlined />}
          onClick={() => onLegendExpandedChange(!legendExpanded)}
          aria-expanded={legendExpanded}
          style={{ padding: compact ? '0 2px' : '0 4px', minWidth: compact ? 20 : 24, height: compact ? 20 : 24 }}
        />
      </Tooltip>
    </div>
  );
};
